import {
  DeckBudget,
  DeckElision,
  DeckModel,
  DeckShapeModel,
  DeckSlideModel,
  DEFAULT_DECK_BUDGET,
  SLIDE_DETAIL_BUDGET,
  TABLE_SHAPE_TYPE,
  admitText,
  describeDeckElision,
  normaliseShapeText,
  shapeCanHoldText,
  shapeSegmentId,
  slideSegmentId,
} from "./deckModel";
import { ToolError } from "./locate";

// Reading the live presentation.
//
// This happens in two passes over two separate `PowerPoint.run` calls, which is not the
// obvious shape and is worth explaining.
//
// The first pass reads the skeleton -- slide ids, shape ids, names and types -- and must
// succeed, because without it there is nothing to show at all. The second pass reads the
// contents: shape text and table cells. That pass is the one that can fail on a deck we
// have never seen, because `Shape.textFrame` throws on a shape with no text frame and a
// batched load turns one such shape into a total failure (see the allowlist in
// deckModel.ts). Splitting the passes means a content failure can be retried
// slide-by-slide and degrade to "this slide's text is unreadable" instead of losing the
// whole deck.
//
// The retry has to be a fresh `PowerPoint.run` rather than another sync on the same
// context, because a sync that threw leaves nothing usable behind. Client objects do not
// cross a run boundary either, which is why the skeleton records ids: the second pass
// re-fetches everything by id with `getItem`.

/** Slide/shape identity from the first pass, in plain data that survives a run boundary. */
interface ShapeSkeleton {
  shapeId: string;
  name: string;
  type: string;
}

interface SlideSkeleton {
  slideId: string;
  /** Zero-based position in the presentation, which is where segment IDs come from. */
  slideIndex: number;
  layout?: string;
  shapes: ShapeSkeleton[];
}

interface DeckSkeleton {
  slideCount: number;
  slides: SlideSkeleton[];
  layouts: string[];
}

/** Contents keyed per shape, filled in by the second pass. */
interface ShapeContents {
  text?: string;
  table?: { rows: number; columns: number; values: string[][] };
}

/**
 * Joined with a NUL, written as an escape rather than a literal character so that this
 * file stays text as far as git and every diff tool are concerned. Office.js documents
 * both ids only as "unique" strings and never their character set, so any printable
 * delimiter could in principle turn up inside one and collide two shapes onto one key.
 * NUL cannot.
 */
const contentsKey = (slideId: string, shapeId: string) => `${slideId}\u0000${shapeId}`;

/**
 * Whether this host can report table contents.
 *
 * A table shape's cells live behind `Shape.getTable()`, which arrived at PowerPointApi
 * 1.8 -- above the 1.7 the manifest requires. Raising the manifest floor to 1.8 would
 * buy tables at the cost of every volume-licensed and older desktop install, which is a
 * bad trade, so this is checked at runtime and the deck says plainly when the answer is
 * no. Read inside the function, never at module scope: `Office.context` is not populated
 * until Office.onReady and does not exist at all under Jest.
 */
const tablesReadable = (): boolean => {
  try {
    return Office.context.requirements.isSetSupported("PowerPointApi", "1.8");
  } catch {
    return false;
  }
};

/**
 * First pass: every slide's identity, its layout name, and the identity of its shapes.
 *
 * `onlySlideNumber` (1-based) narrows it to a single slide for read_slide. Slide
 * *ordinals* come from the position in `presentation.slides`, which Microsoft documents
 * as ordered -- `Slide.index` would say the same thing in one property but is a 1.8 API,
 * so position it is.
 */
const readSkeleton = async (budget: DeckBudget, onlySlideNumber?: number): Promise<DeckSkeleton> =>
  PowerPoint.run(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items/id");
    const masters = context.presentation.slideMasters;
    masters.load("items/id");
    await context.sync();

    const slideCount = slides.items.length;
    const inScope = slides.items
      .map((slide, slideIndex) => ({ slide, slideIndex }))
      .filter(({ slideIndex }) =>
        onlySlideNumber === undefined
          ? slideIndex < budget.maxSlides
          : slideIndex === onlySlideNumber - 1
      );

    for (const { slide } of inScope) {
      slide.layout.load("name");
      slide.shapes.load("items/id, items/name, items/type");
    }

    // Only the first master's layouts are offered to add_slide. A deck with several
    // masters would need the model to disambiguate by master as well as by layout name,
    // which is a lot of prompt for a rare deck; `slides.add` already defaults to the
    // previous slide's master, so the common case is right either way.
    const layoutCollection = masters.items.length > 0 ? masters.items[0].layouts : undefined;
    layoutCollection?.load("items/name");

    await context.sync();

    return {
      slideCount,
      layouts: layoutCollection?.items.map((layout) => layout.name).filter(Boolean) ?? [],
      slides: inScope.map(({ slide, slideIndex }) => ({
        slideId: slide.id,
        slideIndex,
        layout: slide.layout.name || undefined,
        shapes: slide.shapes.items.map((shape) => ({
          shapeId: shape.id,
          name: shape.name || "",
          // `Shape.type` is typed as the enum union, and its values are PascalCase
          // strings ("GeometricShape", not "geometricShape"). Kept as a string so
          // nothing downstream has to touch the PowerPoint global.
          type: String(shape.type),
        })),
      })),
    };
  });

/**
 * Queues the content loads for one slide's shapes and returns handles to harvest after
 * the caller's `context.sync()`. Shared by the batched pass and the per-slide retry so
 * the two cannot drift apart.
 */
const queueContentLoads = (
  context: PowerPoint.RequestContext,
  slide: SlideSkeleton,
  wantTables: boolean
) => {
  const liveSlide = context.presentation.slides.getItem(slide.slideId);
  const textHandles: { shapeId: string; frame: PowerPoint.TextFrame }[] = [];
  const tableHandles: { shapeId: string; table: PowerPoint.Table }[] = [];

  for (const shape of slide.shapes) {
    const liveShape = liveSlide.shapes.getItem(shape.shapeId);

    if (shapeCanHoldText(shape.type)) {
      // Taking a child proxy, not reading a value: no load or sync is owed for the
      // property access itself, and the load that IS owed is the next line. The rule
      // cannot tell the two apart.
      // eslint-disable-next-line office-addins/call-sync-before-read, office-addins/load-object-before-read
      const frame = liveShape.textFrame;
      frame.load("hasText");
      frame.textRange.load("text");
      textHandles.push({ shapeId: shape.shapeId, frame });
    } else if (wantTables && shape.type === TABLE_SHAPE_TYPE) {
      const table = liveShape.getTable();
      table.load("rowCount, columnCount, values");
      tableHandles.push({ shapeId: shape.shapeId, table });
    }
  }

  return { textHandles, tableHandles };
};

const harvestContents = (
  slide: SlideSkeleton,
  handles: ReturnType<typeof queueContentLoads>,
  into: Map<string, ShapeContents>
) => {
  for (const { shapeId, frame } of handles.textHandles) {
    if (!frame.hasText) continue;
    const text = normaliseShapeText(frame.textRange.text ?? "");
    if (text.length > 0) into.set(contentsKey(slide.slideId, shapeId), { text });
  }

  for (const { shapeId, table } of handles.tableHandles) {
    const key = contentsKey(slide.slideId, shapeId);
    into.set(key, {
      ...into.get(key),
      table: {
        rows: table.rowCount,
        columns: table.columnCount,
        values: (table.values ?? []).map((row) => row.map(normaliseShapeText)),
      },
    });
  }
};

/**
 * Second pass: shape text and table cells for the whole skeleton in one round trip,
 * falling back to one round trip per slide if that fails.
 *
 * Returns the slides (1-based) it could not read, for the payload's notice.
 */
const readContents = async (
  skeleton: DeckSkeleton,
  wantTables: boolean,
  into: Map<string, ShapeContents>
): Promise<number[]> => {
  try {
    await PowerPoint.run(async (context) => {
      const perSlide = skeleton.slides.map((slide) => ({
        slide,
        handles: queueContentLoads(context, slide, wantTables),
      }));
      await context.sync();
      for (const { slide, handles } of perSlide) harvestContents(slide, handles, into);
    });
    return [];
  } catch {
    // One unreadable shape somewhere took the batch down. Retry per slide so the damage
    // is confined to whichever slide actually holds it.
  }

  const unreadable: number[] = [];
  for (const slide of skeleton.slides) {
    try {
      await PowerPoint.run(async (context) => {
        const handles = queueContentLoads(context, slide, wantTables);
        // A sync per slide, deliberately. This is the fallback after the batched pass
        // already failed, and its whole purpose is to isolate the one slide holding an
        // unreadable shape -- which cannot be done in a single batch, since one throw
        // takes the batch with it. Slow, and only on a path that is already degraded.
        // eslint-disable-next-line office-addins/no-context-sync-in-loop
        await context.sync();
        harvestContents(slide, handles, into);
      });
    } catch {
      unreadable.push(slide.slideIndex + 1);
    }
  }
  return unreadable;
};

interface BuildOptions {
  budget: DeckBudget;
  tablesAvailable: boolean;
  /**
   * Slides left out because the budget ran out. Passed in rather than derived from the
   * skeleton, because read_slide also returns one slide out of many and that is a
   * deliberate answer to a question, not an elision to warn about.
   */
  slidesOmitted: number;
}

const buildDeckModel = (
  skeleton: DeckSkeleton,
  contents: Map<string, ShapeContents>,
  unreadableSlides: number[],
  { budget, tablesAvailable, slidesOmitted }: BuildOptions
): DeckModel => {
  const elision: DeckElision = {
    slidesOmitted,
    shapesTruncated: 0,
    shapesWithheld: 0,
    slidesUnreadable: unreadableSlides,
    tablesUnavailable: false,
  };
  let charsSpent = 0;

  const slides: DeckSlideModel[] = skeleton.slides.map((slide) => {
    const shapes: DeckShapeModel[] = slide.shapes.map((shape) => {
      const found = contents.get(contentsKey(slide.slideId, shape.shapeId));
      const admission = admitText(found?.text ?? "", charsSpent, budget);
      charsSpent += admission.charsSpent;

      if (admission.truncated) elision.shapesTruncated += 1;
      if (admission.withheld) elision.shapesWithheld += 1;
      if (shape.type === TABLE_SHAPE_TYPE && !tablesAvailable) elision.tablesUnavailable = true;

      return {
        segmentId: shapeSegmentId(slide.slideIndex, shape.shapeId),
        shapeId: shape.shapeId,
        name: shape.name,
        type: shape.type,
        ...(admission.text !== undefined ? { text: admission.text } : {}),
        ...(admission.truncated ? { textTruncated: true as const } : {}),
        ...(admission.withheld ? { textWithheld: true as const } : {}),
        ...(found?.table ? { table: found.table } : {}),
      };
    });

    const isUnreadable = unreadableSlides.includes(slide.slideIndex + 1);
    return {
      segmentId: slideSegmentId(slide.slideIndex),
      slideId: slide.slideId,
      ...(slide.layout ? { layout: slide.layout } : {}),
      shapes,
      ...(isUnreadable ? { textUnreadable: true as const } : {}),
    };
  });

  const notice = describeDeckElision(elision);

  return {
    slideCount: skeleton.slideCount,
    slides,
    ...(skeleton.layouts.length > 0 ? { layouts: skeleton.layouts } : {}),
    ...(notice ? { notice } : {}),
  };
};

/**
 * The whole presentation, as the chat's per-turn document state.
 *
 * Whole by default: a deck is not a spreadsheet, and sixty slides of bullet points is a
 * Word-sized document. The budget in deckModel.ts only bites on the pathological deck,
 * and says so in the payload when it does.
 */
export const extractDeck = async (budget: DeckBudget = DEFAULT_DECK_BUDGET): Promise<DeckModel> => {
  const skeleton = await readSkeleton(budget);
  const wantTables = tablesReadable();
  const contents = new Map<string, ShapeContents>();
  const unreadable = await readContents(skeleton, wantTables, contents);
  return buildDeckModel(skeleton, contents, unreadable, {
    budget,
    tablesAvailable: wantTables,
    slidesOmitted: Math.max(0, skeleton.slideCount - skeleton.slides.length),
  });
};

/**
 * One slide, in full, with nothing withheld -- the escape hatch behind the read_slide
 * tool. Returns the same DeckModel shape (with a single-slide `slides` array) so the
 * model reads one vocabulary rather than two.
 */
export const extractSlideDetail = async (slideNumber: number): Promise<DeckModel> => {
  const skeleton = await readSkeleton(SLIDE_DETAIL_BUDGET, slideNumber);
  if (skeleton.slides.length === 0) {
    // A ToolError rather than a bare Error so read_slide reports "that slide does not
    // exist" to the model instead of dressing it up as a PowerPoint API failure.
    throw new ToolError(
      `Slide ${slideNumber} does not exist; the presentation has ${skeleton.slideCount} slide(s).`
    );
  }

  const wantTables = tablesReadable();
  const contents = new Map<string, ShapeContents>();
  const unreadable = await readContents(skeleton, wantTables, contents);
  return buildDeckModel(skeleton, contents, unreadable, {
    budget: SLIDE_DETAIL_BUDGET,
    tablesAvailable: wantTables,
    slidesOmitted: 0,
  });
};
